import {html, css} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import tippy, {Instance as TippyInstance} from 'tippy.js';
import Decimal from './break_infinity.js';
import global_css from './global-css.js';

import {UnlockableLitElement} from './unlockable.js';
import {formatDelta, formatStat, formatStatDelta} from './utils.js';

@customElement('ri-stat')
export class Stat extends UnlockableLitElement {
  @property() override name = 'stat';
  @property() override displayName = 'Stat';
  @property() shortName = '???';
  @property() description = 'Description';
  override _tags = new Set(['stat']);
  @property({type: Decimal}) _value = new Decimal(1);
  get value() {
    return this._value;
  }
  set value(value) {
    if (value.lt(this.min)) value = this.min;
    this.beforeChange(value);
    this._value = value;
  }
  min = new Decimal(1);
  @property({type: Decimal}) delta = new Decimal(0);
  tooltip: TippyInstance;
  initialValue: Decimal = new Decimal(0);

  public init(value) {
    this.value = new Decimal(value);
    this.setInitials();
  }

  override setInitials() {
    this.initialValue = this.value;
  }

  override resetToInitials() {
    super.resetToInitials();
    this.value = this.initialValue;
  }

  constructor() {
    super();
    this.tooltip = tippy(this, {
      content: `${this.description}`,
      placement: 'top-start',
      allowHTML: true,
    });
  }

  beforeChange: (newValue: Decimal) => void = (_newValue: Decimal) => {};

  get valueHTML() {
    return this.getValueHTML();
  }
  getValueHTML(fancy = true, named = true) {
    return `<span><span class='${this.name} ${fancy ? 'fancy' : ''} stat'>
    ${formatStat(this.value)}</span> ${named ? this.shortName : ''}</span>`;
  }

  static override styles = [
    global_css,
    css`
      :host {
        min-width: 7ch;
        height: 4em;
      }
      .container {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        padding-bottom: 4px;
      }
      .container * {
        min-height: 1fr;
      }
      .value {
        font-size: 1.4rem;
      }
    `,
  ];
  override render() {
    this.tooltip.setContent(
      `<strong>${this.displayName}</strong><br>${this.description} `
    );
    const style = getComputedStyle(this);
    return html`
      <div class=${this.name + ' fancy container'}>
        <div class="value">${unsafeHTML(this.getValueHTML(false, false))}</div>
        <div class="short-name">${this.shortName}</div>
        <div class="delta">${formatStatDelta(this.delta)}</div>
      </div>
    `;
  }
}
